<template>
    <div
        v-if="!isLoading && performance.length !== 0"
        class="performance-graph bg-white rounded-borders"
    >
        <v-chart :option="chartOptions" autoresize></v-chart>
    </div>

    <div v-else-if="isLoading" class="text-center q-ma-xl">
        <q-spinner color="primary" size="3em" />
    </div>
</template>
<script>
import VChart from 'vue-echarts';
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { LineChart } from 'echarts/charts';
import {
    TooltipComponent,
    VisualMapComponent,
    MarkAreaComponent,
    GridComponent
} from 'echarts/components';

use([
    CanvasRenderer,
    LineChart,
    TooltipComponent,
    VisualMapComponent,
    MarkAreaComponent,
    GridComponent
]);
import { MAX_GRAPH_PERFORMANCE_VALUE } from '@pages/AssetDashboard/PerformanceGraph.js';
import { sanitizeString } from '@utils/sanitize.js';

export default {
    name: 'PerformanceGraph',
    components: { VChart },
    props: {
        /**
         * Performance data.
         */
        performance: {
            type: Array,
            required: true
        },
        /**
         * Whether the graph is loading.
         */
        isLoading: {
            type: Boolean,
            required: false,
            default: false
        },
        events: {
            type: Array,
            required: false,
            default: () => []
        }
    },
    computed: {
        /**
         * Get the chart definition for the echart component.
         */
        chartOptions() {
            return {
                color: ['#000000'],
                tooltip: {
                    trigger: 'axis',
                    formatter: params => {
                        params = params[0];
                        // see getTooltipContent method comments for more details
                        const data = {
                            date: new Date(params.value[0])
                                .toLocaleString('en-GB')
                                .slice(0, -3),
                            actualPerformance: params.value[2]
                        };
                        return this.getTooltipContent(data);
                    }
                },
                grid: {
                    left: '40px',
                    right: '20px',
                    top: '20px',
                    bottom: '15px',
                    outerBoundsMode: 'same',
                    outerBoundsContain: 'axisLabel'
                },

                xAxis: {
                    type: 'time',
                    boundaryGap: false,
                    splitLine: {
                        show: false
                    },
                    maxInterval: this.$q.screen.lt.sm
                        ? 3800 * 1000
                        : 3600 * 1000
                },
                yAxis: [
                    {
                        type: 'value',
                        minInterval: 25,
                        name: this.$t('Performance'),
                        nameLocation: 'middle',
                        nameGap: 50,
                        splitLine: {
                            show: false
                        },
                        // The minimum value of the y-axis is set to 0, to ensure that the graph does not start from a negative value.
                        min: 0,
                        axisLabel: {
                            formatter: function (value) {
                                const maxPerformanceValue =
                                    MAX_GRAPH_PERFORMANCE_VALUE * 100;
                                return value === maxPerformanceValue
                                    ? `> ${maxPerformanceValue}%`
                                    : `${value}%`;
                            },
                            hideOverlap: true
                        }
                    }
                ],
                series: [
                    {
                        name: this.$t('Performance'),
                        type: 'line',
                        smooth: false,
                        showSymbol: false,
                        data: this.performance,
                        markArea: {
                            itemStyle: {
                                color: 'rgba(200, 50, 50,0.5)'
                            },
                            data: this.downtimeEventsData
                        }
                    }
                ],
                animation: false
            };
        },
        downtimeEventsData() {
            let formattedEvents = [];

            for (let event of this.events) {
                // if the event is a downtime event then only add it to the graph
                if (event.phase === 'in_downtime') {
                    formattedEvents.push([
                        {
                            xAxis: this.formatDate(event.start)
                        },
                        {
                            xAxis: this.formatDate(event.end)
                        }
                    ]);
                }
            }
            return formattedEvents;
        }
    },
    methods: {
        /**
         * The timezone conversion does not happened correctly
         * if there are milliseconds present in the date-time
         * string. This method helps in removing the milliseconds
         * if present.
         * @param {String} date - Date-time string.
         * @return {String} - Formatted date-time string.
         */
        formatDate(date) {
            let formattedDate = date;
            if (formattedDate && formattedDate.includes('.')) {
                let startTimeArray = formattedDate.split('.');
                formattedDate = startTimeArray[0] + 'Z';
            }
            return formattedDate;
        },
        /**
         * Get the tooltip content.
         * If the actual performance exceeds the maximum graph performance
         * value, the graph will display the maximum graph performance value.
         * So, the tooltip will display the actual performance value along with
         * the message that the actual performance exceeds the maximum graph
         * performance value.
         * Limit the graph to a maximum value increase the readability of the
         *  graph in case of spikes in performance. See addPerformancePoint
         * method in PerformanceGraph.js for more details.
         * @param {Object} data - The data object.
         * @returns {String} - The tooltip content.
         */
        getTooltipContent({ date, actualPerformance }) {
            // The actual performance value is multiplied by 100 to convert it to percentage.
            const maxPerformanceValue = MAX_GRAPH_PERFORMANCE_VALUE * 100;
            const escDate = sanitizeString(date);
            const escActual = sanitizeString(actualPerformance);

            if (actualPerformance > maxPerformanceValue) {
                const exceeds = this.$t('Exceeds {value}%', {
                    value: maxPerformanceValue
                });
                const actualLabel = this.$t('Actual Value:');
                return (
                    escDate +
                    '<br>' +
                    exceeds +
                    '<br>' +
                    actualLabel +
                    ' ' +
                    escActual +
                    '%'
                );
            }
            return escDate + '<br>' + escActual + '%';
        }
    }
};
</script>

<style lang="scss" scoped>
.echarts {
    width: 100%;
    height: 120px;
}
.performance-graph {
    border: 1px solid $grey-4;
}
</style>
